import { CircleStop, Clock, GitBranch, Maximize2, Minus, Pencil, Plus, Trash2, X, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input, Select } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

/*
 * Ablauf-Editor als Zeichenfläche (wie ein Callflow): Start links, Schritte als Karten nach rechts,
 * Verzweigungen teilen sich in Zweige („Wenn …“ / „Sonst“), danach laufen die Zweige wieder zusammen.
 * Bearbeitet wird im Eigenschaftenfeld rechts (Stift an der Karte).
 */

export type FlowCondition = { field: string; op: string; value: string | number | boolean | string[] };
export type FlowAction = { type: string } & Record<string, unknown>;
export type Step =
  | { kind: 'action'; id: string; action: FlowAction }
  | { kind: 'branch'; id: string; label?: string; branches: Lane[]; otherwise: Step[] }
  | { kind: 'wait'; id: string; minutes: number; label?: string }
  | { kind: 'stop'; id: string; label?: string };
export type Lane = { id: string; label: string; match: 'all' | 'any'; conditions: FlowCondition[]; steps: Step[] };

export const newId = () => Math.random().toString(36).slice(2, 10);
export const MAX_FLOW_DEPTH = 6;

export function countSteps(steps: Step[]) {
  const c = { total: 0, branches: 0, waits: 0, actions: 0 };
  const walk = (list: Step[]) => {
    for (const s of list) {
      c.total++;
      if (s.kind === 'action') c.actions++;
      if (s.kind === 'wait') c.waits++;
      if (s.kind === 'branch') {
        c.branches++;
        s.branches.forEach((b) => walk(b.steps));
        walk(s.otherwise);
      }
    }
  };
  walk(steps);
  return c;
}

export const formatWait = (min: number) => (min % 1440 === 0 ? `${min / 1440} Tag${min === 1440 ? '' : 'e'}` : min % 60 === 0 ? `${min / 60} Std.` : `${min} Min.`);

// ---------------------------------------------------------------------------
// Baumoperationen
// ---------------------------------------------------------------------------

/** Ziel einer Schrittliste: Hauptablauf, ein Zweig oder „Sonst“ einer Verzweigung. */
type ListRef = { branchId: null } | { branchId: string; laneId: string | 'otherwise' };

function mapLists(steps: Step[], fn: (list: Step[], ref: ListRef) => Step[], ref: ListRef = { branchId: null }): Step[] {
  const mapped = fn(steps, ref);
  return mapped.map((s) =>
    s.kind === 'branch'
      ? {
          ...s,
          branches: s.branches.map((b) => ({ ...b, steps: mapLists(b.steps, fn, { branchId: s.id, laneId: b.id }) })),
          otherwise: mapLists(s.otherwise, fn, { branchId: s.id, laneId: 'otherwise' }),
        }
      : s,
  );
}
const sameRef = (a: ListRef, b: ListRef) => a.branchId === b.branchId && (a.branchId === null || (b.branchId !== null && 'laneId' in a && 'laneId' in b && a.laneId === b.laneId));

function findStep(steps: Step[], id: string): Step | null {
  for (const s of steps) {
    if (s.id === id) return s;
    if (s.kind === 'branch') {
      for (const b of s.branches) {
        const f = findStep(b.steps, id);
        if (f) return f;
      }
      const f = findStep(s.otherwise, id);
      if (f) return f;
    }
  }
  return null;
}
const replaceStep = (steps: Step[], id: string, next: Step) => mapLists(steps, (list) => list.map((s) => (s.id === id ? next : s)));
const removeStep = (steps: Step[], id: string) => mapLists(steps, (list) => list.filter((s) => s.id !== id));
const moveStep = (steps: Step[], id: string, dir: -1 | 1) =>
  mapLists(steps, (list) => {
    const i = list.findIndex((s) => s.id === id);
    if (i < 0 || i + dir < 0 || i + dir >= list.length) return list;
    const next = [...list];
    [next[i], next[i + dir]] = [next[i + dir]!, next[i]!];
    return next;
  });
const appendTo = (steps: Step[], ref: ListRef, step: Step) => mapLists(steps, (list, r) => (sameRef(r, ref) ? [...list, step] : list));
const insertBefore = (steps: Step[], id: string, step: Step) =>
  mapLists(steps, (list) => {
    const i = list.findIndex((s) => s.id === id);
    return i < 0 ? list : [...list.slice(0, i), step, ...list.slice(i)];
  });
function depthOf(steps: Step[], ref: ListRef): number {
  if (ref.branchId === null) return 1;
  let found = 0;
  const walk = (list: Step[], depth: number) => {
    for (const s of list) {
      if (s.kind !== 'branch') continue;
      if (s.id === ref.branchId) found = depth + 1;
      s.branches.forEach((b) => walk(b.steps, depth + 1));
      walk(s.otherwise, depth + 1);
    }
  };
  walk(steps, 1);
  return found || 1;
}
function listOfStep(steps: Step[], id: string): { list: Step[]; ref: ListRef } | null {
  let out: { list: Step[]; ref: ListRef } | null = null;
  mapLists(steps, (list, ref) => {
    if (list.some((s) => s.id === id)) out = { list, ref };
    return list;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const NODE_W = 236;
const NODE_H = 104;
const GAP_X = 56;
const LABEL_W = 132;
const LABEL_H = 40;
const LANE_GAP = 28;
const START = 72;

type Pt = { x: number; y: number };
type LNode = { kind: 'step'; step: Step; x: number; y: number } | { kind: 'lane'; branch: Extract<Step, { kind: 'branch' }>; lane: Lane | null; x: number; y: number };
type LPlus = { x: number; y: number; ref: ListRef; depth: number };
interface Layout {
  nodes: LNode[];
  edges: { from: Pt; to: Pt }[];
  dots: Pt[];
  plus: LPlus[];
  width: number;
  height: number;
}

/** Ordnet eine Schrittliste ab (x, y) an; Eingang ist `entry`. Liefert Ausgang, Breite und Höhe. */
function layoutSeq(steps: Step[], x: number, y: number, entry: Pt, ref: ListRef, depth: number, out: Layout): { exit: Pt; right: number; bottom: number } {
  let cx = x;
  let prev = entry;
  let bottom = y + NODE_H;
  let stopped = false;
  for (const s of steps) {
    const mid = y + NODE_H / 2;
    out.nodes.push({ kind: 'step', step: s, x: cx, y });
    out.edges.push({ from: prev, to: { x: cx, y: mid } });
    if (s.kind === 'stop') {
      stopped = true;
      prev = { x: cx + NODE_W, y: mid };
      cx += NODE_W + GAP_X;
      continue;
    }
    if (s.kind !== 'branch') {
      prev = { x: cx + NODE_W, y: mid };
      cx += NODE_W + GAP_X;
      continue;
    }
    // Verzweigung: Zweige untereinander, erster auf Höhe der Karte
    const split: Pt = { x: cx + NODE_W + GAP_X / 2, y: mid };
    out.edges.push({ from: { x: cx + NODE_W, y: mid }, to: split });
    out.dots.push(split);
    const laneX = cx + NODE_W + GAP_X;
    let ly = y;
    const exits: Pt[] = [];
    let right = laneX + LABEL_W;
    const lanes: (Lane | null)[] = [...s.branches, null];
    for (const lane of lanes) {
      const ly0 = ly + (NODE_H - LABEL_H) / 2;
      out.nodes.push({ kind: 'lane', branch: s, lane, x: laneX, y: ly0 });
      out.edges.push({ from: split, to: { x: laneX, y: ly + NODE_H / 2 } });
      const laneRef: ListRef = { branchId: s.id, laneId: lane?.id ?? 'otherwise' };
      const r = layoutSeq(lane ? lane.steps : s.otherwise, laneX + LABEL_W + GAP_X, ly, { x: laneX + LABEL_W, y: ly + NODE_H / 2 }, laneRef, depth + 1, out);
      exits.push(r.exit);
      right = Math.max(right, r.right);
      ly = r.bottom + LANE_GAP;
      bottom = Math.max(bottom, r.bottom);
    }
    // Zusammenführen hinter dem längsten Zweig
    const join: Pt = { x: right + GAP_X / 2, y: mid };
    for (const e of exits) if (e) out.edges.push({ from: e, to: join });
    out.dots.push(join);
    prev = join;
    cx = join.x + GAP_X / 2;
  }
  // „+“ am Ende der Liste (nicht nach „Ende“)
  if (!stopped) {
    out.plus.push({ x: prev.x + 22, y: prev.y, ref, depth });
    out.edges.push({ from: prev, to: { x: prev.x + 10, y: prev.y } });
  }
  return { exit: stopped ? (null as unknown as Pt) : { x: prev.x + 34, y: prev.y }, right: Math.max(cx - GAP_X, prev.x + 40), bottom };
}

function computeLayout(steps: Step[]): Layout {
  const out: Layout = { nodes: [], edges: [], dots: [], plus: [], width: 0, height: 0 };
  const y = 40;
  const r = layoutSeq(steps, START * 2 + 40, y, { x: START * 2 + 8, y: y + NODE_H / 2 }, { branchId: null }, 1, out);
  out.width = r.right + 120;
  out.height = r.bottom + 60;
  return out;
}

const path = (a: Pt, b: Pt) => (a.y === b.y ? `M${a.x},${a.y} H${b.x}` : `M${a.x},${a.y} H${(a.x + b.x) / 2} V${b.y} H${b.x}`);

// ---------------------------------------------------------------------------
// Darstellung
// ---------------------------------------------------------------------------

const STEP_META = {
  action: { icon: Zap, title: 'Aktion', tone: 'text-primary bg-primary/10' },
  branch: { icon: GitBranch, title: 'Verzweigung', tone: 'text-violet-600 bg-violet-500/10 dark:text-violet-300' },
  wait: { icon: Clock, title: 'Warten', tone: 'text-amber-600 bg-amber-500/10 dark:text-amber-300' },
  stop: { icon: CircleStop, title: 'Ende', tone: 'text-muted-foreground bg-muted' },
} as const;

export interface FlowCanvasProps {
  steps: Step[];
  onChange: (steps: Step[]) => void;
  /** Bezeichnung des Auslösers unter „Start“ */
  triggerLabel: string;
  newStep: (kind: Step['kind']) => Step;
  /** Titel und Kurzbeschreibung einer Aktion für die Karte */
  describeAction: (a: FlowAction) => { title: string; lines: string[] };
  /** Kurzbeschreibung einer Bedingung für die Zweigbeschriftung */
  describeCondition: (c: FlowCondition) => string;
  renderActionEditor: (action: FlowAction, onChange: (a: FlowAction) => void) => ReactNode;
  renderConditions: (conditions: FlowCondition[], onChange: (c: FlowCondition[]) => void) => ReactNode;
}

function AddMenu({ onAdd, allowBranch, children }: { onAdd: (k: Step['kind']) => void; allowBranch: boolean; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => onAdd('action')}>
          <Zap /> Aktion
        </DropdownMenuItem>
        {allowBranch && (
          <DropdownMenuItem onSelect={() => onAdd('branch')}>
            <GitBranch /> Verzweigung (Wenn … / Sonst)
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => onAdd('wait')}>
          <Clock /> Warten
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onAdd('stop')}>
          <CircleStop /> Ende
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const WAIT_UNITS = [
  { value: 1, label: 'Minuten' },
  { value: 60, label: 'Stunden' },
  { value: 1440, label: 'Tage' },
];

export function FlowCanvas(p: FlowCanvasProps) {
  const { steps, onChange } = p;
  const layout = useMemo(() => computeLayout(steps), [steps]);
  const [selected, setSelected] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Pt>({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const sel = selected ? findStep(steps, selected) : null;
  const owner = selected ? listOfStep(steps, selected) : null;

  useEffect(() => {
    if (selected && !sel) setSelected(null);
  }, [selected, sel]);

  /** Ausschnitt so wählen, dass der Ablauf passt (höchstens 100 %), und mittig ausrichten. */
  const fit = useCallback(
    (maxZoom = 1) => {
      const el = box.current;
      if (!el) return;
      const z = Math.round(Math.max(0.25, Math.min(maxZoom, (el.clientWidth - 32) / layout.width, (el.clientHeight - 80) / layout.height)) * 10) / 10;
      setZoom(z);
      setPan({ x: Math.max(8, (el.clientWidth - layout.width * z) / 2), y: Math.max(8, (el.clientHeight - 56 - layout.height * z) / 2) });
    },
    [layout.width, layout.height],
  );
  // Beim Öffnen: 100 %, senkrecht mittig, links beginnend
  useEffect(() => {
    const el = box.current;
    if (el) setPan({ x: 8, y: Math.max(8, (el.clientHeight - 56 - layout.height) / 2) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = (ref: ListRef, kind: Step['kind']) => {
    const s = p.newStep(kind);
    onChange(appendTo(steps, ref, s));
    setSelected(s.id);
  };
  const update = (s: Step) => onChange(replaceStep(steps, s.id, s));

  return (
    <div className="relative h-full min-h-[420px] overflow-hidden rounded-lg border bg-muted/20">
      <div
        ref={box}
        className="absolute inset-0 cursor-grab touch-none select-none active:cursor-grabbing"
        style={{ backgroundImage: 'radial-gradient(circle, color-mix(in oklab, currentColor 18%, transparent) 1px, transparent 1px)', backgroundSize: `${20 * zoom}px ${20 * zoom}px`, backgroundPosition: `${pan.x}px ${pan.y}px` }}
        onPointerDown={(e) => {
          if ((e.target as HTMLElement).closest('[data-node]')) return;
          drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (d) setPan({ x: d.px + e.clientX - d.x, y: d.py + e.clientY - d.y });
        }}
        onPointerUp={() => (drag.current = null)}
        onWheel={(e) => {
          if (!e.ctrlKey && !e.metaKey) {
            setPan((v) => ({ x: v.x - e.deltaX, y: v.y - e.deltaY }));
            return;
          }
          setZoom((z) => Math.min(1.6, Math.max(0.25, Math.round((z - e.deltaY / 500) * 100) / 100)));
        }}
      >
        <div className="absolute left-0 top-0 origin-top-left" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, width: layout.width, height: layout.height }}>
          <svg className="pointer-events-none absolute inset-0 overflow-visible text-primary/70" width={layout.width} height={layout.height} aria-hidden>
            {layout.edges.map((e, i) => (
              <path key={i} d={path(e.from, e.to)} fill="none" stroke="currentColor" strokeWidth={1.5} />
            ))}
            {layout.dots.map((d, i) => (
              <circle key={i} cx={d.x} cy={d.y} r={4} fill="currentColor" />
            ))}
          </svg>

          {/* Start */}
          <div className="absolute flex flex-col items-center gap-1" style={{ left: START - 36, top: 40 + NODE_H / 2 - 36 }}>
            <div className="flex size-[72px] items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow">Start</div>
          </div>
          <p className="absolute w-36 text-center text-[11px] leading-tight text-muted-foreground" style={{ left: START - 72, top: 40 + NODE_H / 2 + 42 }}>
            {p.triggerLabel}
          </p>
          <svg className="pointer-events-none absolute overflow-visible text-primary/70" style={{ left: 0, top: 0 }} aria-hidden>
            <path d={`M${START + 36},${40 + NODE_H / 2} H${START * 2 + 8}`} fill="none" stroke="currentColor" strokeWidth={1.5} />
          </svg>

          {layout.nodes.map((n) => {
            if (n.kind === 'lane') {
              const label = n.lane ? n.lane.label || 'Wenn' : 'Sonst';
              const hint = n.lane ? n.lane.conditions.map(p.describeCondition).join(n.lane.match === 'any' ? ' oder ' : ' und ') : 'trifft kein Zweig zu';
              return (
                <button
                  key={`${n.branch.id}-${n.lane?.id ?? 'else'}`}
                  data-node
                  type="button"
                  title={hint}
                  onClick={() => setSelected(n.branch.id)}
                  className={cn(
                    'absolute flex items-center justify-center gap-1 truncate border bg-background px-2 text-center text-xs shadow-sm hover:border-primary',
                    selected === n.branch.id && 'border-primary ring-1 ring-primary',
                  )}
                  style={{ left: n.x, top: n.y, width: LABEL_W, height: LABEL_H }}
                >
                  {n.lane ? (
                    <span className="truncate">
                      Wenn <strong>{label === 'Wenn' ? hint : label}</strong>
                    </span>
                  ) : (
                    <strong>Sonst</strong>
                  )}
                </button>
              );
            }
            const s = n.step;
            const meta = STEP_META[s.kind];
            const Icon = meta.icon;
            const d =
              s.kind === 'action'
                ? p.describeAction(s.action)
                : s.kind === 'wait'
                  ? { title: s.label || 'Warten', lines: [`Dauer ist ${formatWait(s.minutes)}`, 'dann mit aktuellem Stand weiter'] }
                  : s.kind === 'stop'
                    ? { title: s.label || 'Ende', lines: ['Ablauf endet hier'] }
                    : { title: s.label || 'Verzweigung', lines: [`${s.branches.length} Zweig${s.branches.length === 1 ? '' : 'e'} und „Sonst“`] };
            return (
              <div
                key={s.id}
                data-node
                className={cn('absolute flex flex-col rounded-lg border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md', selected === s.id && 'border-primary ring-1 ring-primary')}
                style={{ left: n.x, top: n.y, width: NODE_W, height: NODE_H }}
              >
                <div className="flex items-center gap-2 border-b px-3 py-2">
                  <span className={cn('flex size-6 shrink-0 items-center justify-center rounded-md [&_svg]:size-3.5', meta.tone)}>
                    <Icon />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{d.title}</span>
                    {s.kind !== 'action' || d.title !== meta.title ? <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">{meta.title}</span> : null}
                  </span>
                  <button
                    type="button"
                    className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 [&_svg]:size-3.5"
                    aria-label={`${d.title} bearbeiten`}
                    onClick={() => setSelected(s.id)}
                  >
                    <Pencil />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden px-3 py-1.5 text-xs text-muted-foreground">
                  {d.lines.slice(0, 2).map((l, i) => (
                    <p key={i} className="truncate">
                      {l}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}

          {layout.plus.map((pl, i) => (
            <div key={i} data-node className="absolute" style={{ left: pl.x - 12, top: pl.y - 12 }}>
              <AddMenu onAdd={(k) => add(pl.ref, k)} allowBranch={pl.depth < MAX_FLOW_DEPTH}>
                <button type="button" className="flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow hover:scale-110 [&_svg]:size-3.5" aria-label="Schritt hinzufügen">
                  <Plus />
                </button>
              </AddMenu>
            </div>
          ))}
        </div>
      </div>

      {/* Werkzeugleiste */}
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border bg-background px-2 py-1 shadow-md">
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Verkleinern" onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 10) / 10))}>
          <Minus />
        </Button>
        <span className="w-12 text-center text-xs tabular-nums">{Math.round(zoom * 100)} %</span>
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Vergrößern" onClick={() => setZoom((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10))}>
          <Plus />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Einpassen" title="Einpassen" onClick={() => fit()}>
          <Maximize2 />
        </Button>
      </div>

      {/* Eigenschaften */}
      {sel && owner && (
        <aside className="absolute inset-y-0 right-0 z-10 flex w-full max-w-[420px] flex-col border-l bg-background shadow-xl" aria-label="Eigenschaften">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <span className="flex-1 font-semibold">{STEP_META[sel.kind].title} bearbeiten</span>
            <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Eigenschaften schließen" onClick={() => setSelected(null)}>
              <X />
            </Button>
          </div>
          <div className="grid min-h-0 flex-1 content-start gap-4 overflow-y-auto p-4 text-sm">
            {sel.kind === 'action' && p.renderActionEditor(sel.action, (a) => update({ ...sel, action: a }))}
            {sel.kind === 'wait' && (
              <>
                <Input value={sel.label ?? ''} placeholder="Bezeichnung (optional)" onChange={(e) => update({ ...sel, label: e.target.value })} aria-label="Bezeichnung" />
                <WaitFields step={sel} onChange={update} />
              </>
            )}
            {sel.kind === 'stop' && <Input value={sel.label ?? ''} placeholder="Bezeichnung (optional)" onChange={(e) => update({ ...sel, label: e.target.value })} aria-label="Bezeichnung" />}
            {sel.kind === 'branch' && <BranchFields step={sel} onChange={update} newLane={() => (p.newStep('branch') as Extract<Step, { kind: 'branch' }>).branches[0]!} renderConditions={p.renderConditions} />}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t px-4 py-3">
            <AddMenu onAdd={(k) => {
              const s = p.newStep(k);
              onChange(insertBefore(steps, sel.id, s));
              setSelected(s.id);
            }} allowBranch={depthOf(steps, owner.ref) < MAX_FLOW_DEPTH}>
              <Button type="button" size="sm" variant="outline">
                <Plus /> Davor einfügen
              </Button>
            </AddMenu>
            <Button type="button" size="sm" variant="outline" disabled={owner.list[0]?.id === sel.id} onClick={() => onChange(moveStep(steps, sel.id, -1))}>
              Nach vorn
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={owner.list.at(-1)?.id === sel.id} onClick={() => onChange(moveStep(steps, sel.id, 1))}>
              Nach hinten
            </Button>
            <Button type="button" size="sm" variant="ghost" className="ml-auto text-destructive hover:text-destructive" onClick={() => onChange(removeStep(steps, sel.id))}>
              <Trash2 /> Entfernen
            </Button>
          </div>
        </aside>
      )}

      {steps.length === 0 && (
        <p className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 text-sm text-muted-foreground" style={{ marginTop: NODE_H }}>
          Mit dem blauen + den ersten Schritt anlegen.
        </p>
      )}
    </div>
  );
}

function WaitFields({ step, onChange }: { step: Extract<Step, { kind: 'wait' }>; onChange: (s: Step) => void }) {
  const unit = step.minutes % 1440 === 0 ? 1440 : step.minutes % 60 === 0 ? 60 : 1;
  return (
    <div className="grid gap-2">
      <div className="flex gap-2">
        <Input
          type="number"
          className="w-28"
          min={1}
          max={Math.floor(43200 / unit)}
          value={step.minutes / unit}
          onChange={(e) => onChange({ ...step, minutes: Math.min(43200, Math.max(1, Math.round(Number(e.target.value) || 1)) * unit) })}
          aria-label="Dauer"
        />
        <Select value={unit} onChange={(e) => onChange({ ...step, minutes: Math.min(43200, Math.max(1, Math.round(step.minutes / unit)) * Number(e.target.value)) })} aria-label="Einheit">
          {WAIT_UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </Select>
      </div>
      <p className="text-xs text-muted-foreground">Danach geht es mit dem dann aktuellen Stand des Vorgangs weiter (höchstens 30 Tage).</p>
    </div>
  );
}

function BranchFields({
  step,
  onChange,
  newLane,
  renderConditions,
}: {
  step: Extract<Step, { kind: 'branch' }>;
  onChange: (s: Step) => void;
  newLane: () => Lane;
  renderConditions: FlowCanvasProps['renderConditions'];
}) {
  return (
    <div className="grid gap-4">
      <Input value={step.label ?? ''} placeholder="Bezeichnung, z. B. Dringlichkeit" onChange={(e) => onChange({ ...step, label: e.target.value })} aria-label="Bezeichnung der Verzweigung" />
      <p className="text-xs text-muted-foreground">Der erste Zweig, dessen Bedingungen zutreffen, läuft; sonst „Sonst“. Danach geht es hinter der Verzweigung weiter.</p>
      {step.branches.map((b, i) => {
        const set = (patch: Partial<Lane>) => onChange({ ...step, branches: step.branches.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
        return (
          <div key={b.id} className="grid gap-2 rounded-md border-l-4 border-violet-500/60 bg-violet-500/5 p-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">{i === 0 ? 'Wenn' : 'Sonst wenn'}</span>
              <Input className="h-8" value={b.label} onChange={(e) => set({ label: e.target.value })} aria-label="Name des Zweigs" />
              {step.branches.length > 1 && (
                <Button type="button" variant="ghost" size="icon" className="size-8 shrink-0" aria-label={`Zweig ${b.label} entfernen`} onClick={() => onChange({ ...step, branches: step.branches.filter((_, j) => j !== i) })}>
                  <X />
                </Button>
              )}
            </div>
            <Select className="h-8" value={b.match} onChange={(e) => set({ match: e.target.value as Lane['match'] })} aria-label="Verknüpfung">
              <option value="all">alle Bedingungen treffen zu</option>
              <option value="any">mindestens eine trifft zu</option>
            </Select>
            {renderConditions(b.conditions, (conditions) => set({ conditions }))}
          </div>
        );
      })}
      <div>
        <Button type="button" size="sm" variant="outline" disabled={step.branches.length >= 10} onClick={() => onChange({ ...step, branches: [...step.branches, { ...newLane(), label: `Zweig ${step.branches.length + 1}` }] })}>
          <Plus /> Zweig
        </Button>
      </div>
    </div>
  );
}
